import dayjs from 'dayjs';
import { useMemo } from 'react';

import {
  DtoV1EnumTaxPaymentTypes,
  DtoV1PropertyTaxEscrowDetails,
  DtoV1PropertyTaxGetTaxSummaryDto,
  DtoV1PropertyTaxSaveTaxSummaryRequestDto,
  DtoV1PropertyTaxSupplementalInstallment,
  type UWMCLibraryCommonDomainEnumsTaxAnswerSource,
} from '~/__generated__/api';
import { TITLE_ICON_TYPE } from '~/common/componentMapper/QuestionBox/constants';
import { TRACK_TYPE_DESCRIPTIONS } from '~/common/constants';
import {
  USStateAbbreviation,
  US_STATES_BY_ABBREVIATION,
  US_STATES_BY_NAME,
} from '~/common/constants/us-states';
import type {
  LoanSummary,
  PurposeType,
} from '~/common/hooks/useGetLoanSummary';
import { formatCurrency } from '~/common/util/format';
import { REFI_PURPOSE_TYPE } from '~/pages/MortgageHistory/mortgageHistoryUtils';

import {
  DISBURSEMENT_AMOUNT,
  ESCROW_WAIVER_TAX_OPTION_IDS,
  FUTURE_TAX_DISBURSEMENT_OPTIONS,
  LAND_ONLY_AMOUNT,
  PAST_TAX_DISBURSEMENT_OPTIONS,
  SUPPLEMENTAL_TAX_DISBURSEMENT_OPTIONS,
  TAX_DISBURSEMENT_OPTIONS,
  TAX_DOC_TYPES_LABELS,
  TAX_PAYMENT_TYPES,
  WAIVE_ESCROW_OPTION_DISPLAY_NAMES,
} from './constants';
import { TAX_ANSWER_SOURCE } from './taxAnswerSource';
import {
  type TaxesFormValues,
  getInitialFormValues,
  isDirty,
} from './taxFormUtils';

export const formatTaxDate = (date: string | Date) =>
  new Date(date).toLocaleDateString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
  });

const answerSourceToIconType = (
  source: UWMCLibraryCommonDomainEnumsTaxAnswerSource | undefined,
): number => {
  if (source === TAX_ANSWER_SOURCE.CORELOGIC) return TITLE_ICON_TYPE.BOLT;
  if (
    source === TAX_ANSWER_SOURCE.INTERNAL ||
    source === TAX_ANSWER_SOURCE.EXTERNAL
  )
    return TITLE_ICON_TYPE.USER;
  return TITLE_ICON_TYPE.NONE;
};

const shouldShowNewIcon = (
  installmentAmount: number | undefined,
  currentInstallmentAmount: number | null | undefined,
): boolean => {
  if (currentInstallmentAmount == null) return false;
  return installmentAmount !== currentInstallmentAmount;
};

const getIconType = (
  arePropertyTaxesIconsEnabled: boolean | undefined,
  taxAnswerSource: UWMCLibraryCommonDomainEnumsTaxAnswerSource | undefined,
  installmentAmount: number | undefined,
  currentInstallmentAmount: number | null | undefined,
): number => {
  if (!arePropertyTaxesIconsEnabled) return TITLE_ICON_TYPE.NONE;
  const hasNewIcon = shouldShowNewIcon(
    installmentAmount,
    currentInstallmentAmount,
  );
  const sourceIcon = answerSourceToIconType(taxAnswerSource);
  if (hasNewIcon && sourceIcon === TITLE_ICON_TYPE.NONE)
    return TITLE_ICON_TYPE.NEW;
  return sourceIcon;
};

type TaxPaymentOption = { label: string; value: number };

const applyTaxWaiverToPaymentOptions = (
  options: TaxPaymentOption[],
  isTaxEscrowWaived: boolean,
) => {
  if (isTaxEscrowWaived) {
    return options.filter(
      (opt) => opt.value !== TAX_PAYMENT_TYPES.COLLECT_IN_ESCROW,
    );
  }
  return options.filter((opt) => opt.value !== TAX_PAYMENT_TYPES.NOT_ESCROWED);
};

const prioritizePaymentOption = (
  options: TaxPaymentOption[],
  prioritizedValue: number | undefined,
) => {
  if (prioritizedValue === undefined) return options;
  const prioritizedOption = options.find(
    (option) => option.value === prioritizedValue,
  );
  if (!prioritizedOption) return options;
  return [
    prioritizedOption,
    ...options.filter((option) => option.value !== prioritizedValue),
  ];
};

export const getSupplementalTaxDefaultPaidType = ({
  supplementalDate,
  effectiveClosingDate,
  firstPaymentDate,
  isTracLoan,
}: {
  supplementalDate?: string | null;
  effectiveClosingDate?: dayjs.Dayjs | null;
  firstPaymentDate?: dayjs.Dayjs | null;
  isTracLoan?: boolean;
}) => {
  if (isTracLoan || !supplementalDate || !effectiveClosingDate) {
    return undefined;
  }

  const dueDate = dayjs(supplementalDate);
  if (!dueDate.isValid()) return undefined;

  if (firstPaymentDate?.isBefore(dueDate, 'day')) {
    return TAX_PAYMENT_TYPES.COLLECT_IN_ESCROW;
  }

  const twoMonthsBeforeClosing = effectiveClosingDate.subtract(2, 'month');
  if (
    (dueDate.isSame(twoMonthsBeforeClosing, 'day') ||
      dueDate.isAfter(twoMonthsBeforeClosing, 'day')) &&
    (!firstPaymentDate ||
      dueDate.isSame(firstPaymentDate, 'day') ||
      dueDate.isBefore(firstPaymentDate, 'day'))
  ) {
    return TAX_PAYMENT_TYPES.BORROWER_TO_PAY_AT_CLOSING;
  }

  return undefined;
};

export const getSupplementalTaxPaymentOptions = ({
  supplementalDate,
  effectiveClosingDate,
  firstPaymentDate,
  isTracLoan,
  isTaxEscrowWaived,
}: {
  supplementalDate?: string | null;
  effectiveClosingDate?: dayjs.Dayjs | null;
  firstPaymentDate?: dayjs.Dayjs | null;
  isTracLoan?: boolean;
  isTaxEscrowWaived?: boolean;
}) => {
  if (isTracLoan) return SUPPLEMENTAL_TAX_DISBURSEMENT_OPTIONS.TRAC;

  const options = applyTaxWaiverToPaymentOptions(
    SUPPLEMENTAL_TAX_DISBURSEMENT_OPTIONS.NON_TRAC,
    Boolean(isTaxEscrowWaived),
  );
  return prioritizePaymentOption(
    options,
    getSupplementalTaxDefaultPaidType({
      supplementalDate,
      effectiveClosingDate,
      firstPaymentDate,
      isTracLoan,
    }),
  );
};

const getSecondaryIconType = (
  arePropertyTaxesIconsEnabled: boolean | undefined,
  taxAnswerSource: UWMCLibraryCommonDomainEnumsTaxAnswerSource | undefined,
  installmentAmount: number | undefined,
  currentInstallmentAmount: number | null | undefined,
): number => {
  if (!arePropertyTaxesIconsEnabled) return TITLE_ICON_TYPE.NONE;
  const hasNewIcon = shouldShowNewIcon(
    installmentAmount,
    currentInstallmentAmount,
  );
  const sourceIcon = answerSourceToIconType(taxAnswerSource);
  if (hasNewIcon && sourceIcon !== TITLE_ICON_TYPE.NONE)
    return TITLE_ICON_TYPE.NEW;
  return TITLE_ICON_TYPE.NONE;
};

export const generateScreenData = ({
  data,
  loanSummaryInfo,
  arePropertyTaxesIconsEnabled,
}: {
  data: DtoV1PropertyTaxEscrowDetails;
  loanSummaryInfo: LoanSummary | undefined;
  arePropertyTaxesIconsEnabled?: boolean;
}) => {
  if (!data) return {} as never;

  const futureTaxesDisabledState = {};
  const futureTaxesPayTypeOptions = {};
  const purposeType: PurposeType | undefined =
    loanSummaryInfo?.propertyData?.purposeType;
  const isRefinance = loanSummaryInfo?.propertyData?.isRefi ?? false;
  const isPurchase =
    purposeType === 'Purchase Home' ||
    purposeType === 'Purchase second mortgage';
  const appraisedValue = loanSummaryInfo?.loanInfo.appraisedValue ?? 0;
  const salesPrice = loanSummaryInfo?.loanInfo.salesPrice ?? 0;
  const isTrackPlus =
    loanSummaryInfo?.loanInfo.tracTypeDescription ===
    TRACK_TYPE_DESCRIPTIONS.TracPlus;
  const isTracLoan = [
    TRACK_TYPE_DESCRIPTIONS.Trac,
    TRACK_TYPE_DESCRIPTIONS.TracPlus,
    TRACK_TYPE_DESCRIPTIONS.Doma,
  ].includes(loanSummaryInfo?.loanInfo.tracTypeDescription);
  const isTaxEscrowWaived =
    !!data.escrowWaiverOption &&
    (ESCROW_WAIVER_TAX_OPTION_IDS as readonly number[]).includes(
      data.escrowWaiverOption,
    );

  const millageRateHint = isRefinance
    ? `Appraised Value ${formatCurrency(appraisedValue)}`
    : `Purchase Price ${formatCurrency(salesPrice)}`;
  const totalValue = isRefinance ? appraisedValue : salesPrice;

  const applyTaxWaiverToOptions = (options: TaxPaymentOption[]) =>
    applyTaxWaiverToPaymentOptions(options, isTaxEscrowWaived);

  let defaultDisbursementOptions = [];
  if (isTrackPlus) {
    defaultDisbursementOptions = TAX_DISBURSEMENT_OPTIONS.trackPlus;
  } else if (isRefinance) {
    defaultDisbursementOptions = TAX_DISBURSEMENT_OPTIONS.refinance;
  } else {
    defaultDisbursementOptions = TAX_DISBURSEMENT_OPTIONS.purchase;
  }

  defaultDisbursementOptions = applyTaxWaiverToOptions(
    defaultDisbursementOptions,
  );

  let futureDisbursementValue: { label: string; value: number }[];
  if (isTrackPlus) {
    futureDisbursementValue = FUTURE_TAX_DISBURSEMENT_OPTIONS.trackPlus;
  } else if (isPurchase) {
    futureDisbursementValue = FUTURE_TAX_DISBURSEMENT_OPTIONS.purchase;
  } else {
    futureDisbursementValue = FUTURE_TAX_DISBURSEMENT_OPTIONS.others;
  }

  (data.additionalTaxes ?? [])
    .filter(({ isPastInstallment }) => !isPastInstallment)
    .forEach((_, index) => {
      futureTaxesPayTypeOptions[`futureTaxes.${index}.paidType`] =
        applyTaxWaiverToOptions(futureDisbursementValue);
    });

  const addressState = loanSummaryInfo?.propertyData?.propertyAddress?.state;

  const isLoanPropertyInArizona = addressState === US_STATES_BY_NAME.Arizona;

  const isBuiltWitninLastThreeYears =
    loanSummaryInfo?.propertyData?.yearBuilt &&
    new Date().getFullYear() - loanSummaryInfo.propertyData.yearBuilt <= 3;

  const problemStates = [
    US_STATES_BY_NAME.Florida,
    US_STATES_BY_NAME.Texas,
    US_STATES_BY_NAME.California,
    US_STATES_BY_NAME.Illinois,
    US_STATES_BY_NAME.Oregon,
    US_STATES_BY_NAME.Nevada,
  ];

  const showJobAidTip =
    isPurchase &&
    ((isLoanPropertyInArizona && isBuiltWitninLastThreeYears) ||
      problemStates.includes((addressState ?? '') as USStateAbbreviation));

  const installmentsPayTypeOptions = {};
  const questionAnswers: Record<
    string,
    { iconType: number; secondaryIconType: number }
  > = {};
  let hideExemptToggle = true;
  const { estimatedClosingDate, actualClosingDate, firstPaymentDate } =
    loanSummaryInfo.loanSummaryDates;

  const effectiveClosingDate = actualClosingDate ?? estimatedClosingDate;

  (data.escrowInstallments ?? []).forEach(
    (
      {
        distributionDate,
        installmentAmount,
        taxAnswerSource,
        currentInstallmentAmount,
      },
      i,
    ) => {
      if (installmentAmount === 0) {
        hideExemptToggle = false;
      }

      const distribution = dayjs(distributionDate);
      const key = `installments.${i}.paidType`;

      const isBetweenClosingAndFirstPayment =
        firstPaymentDate?.isAfter(distribution) &&
        distribution.isAfter(effectiveClosingDate);

      let value;

      if (isTrackPlus && firstPaymentDate?.isBefore(distribution)) {
        value = applyTaxWaiverToOptions(
          TAX_DISBURSEMENT_OPTIONS.trackPlusAfterFirstPayment,
        );
      } else if (isTrackPlus) {
        value = TAX_DISBURSEMENT_OPTIONS.trackPlus;
      } else if (isBetweenClosingAndFirstPayment) {
        value = applyTaxWaiverToOptions(
          isRefinance
            ? TAX_DISBURSEMENT_OPTIONS.beforeClosingRefi
            : TAX_DISBURSEMENT_OPTIONS.beforeClosing,
        );
      } else if (firstPaymentDate?.isBefore(distribution) && isRefinance) {
        value = applyTaxWaiverToOptions(
          TAX_DISBURSEMENT_OPTIONS.afterFirstPaymentRefi,
        );
      } else {
        value = defaultDisbursementOptions;
      }

      installmentsPayTypeOptions[key] = value;
      questionAnswers[`installments.${i}.installmentAmount`] = {
        iconType: getIconType(
          arePropertyTaxesIconsEnabled,
          taxAnswerSource,
          installmentAmount,
          currentInstallmentAmount,
        ),
        secondaryIconType: getSecondaryIconType(
          arePropertyTaxesIconsEnabled,
          taxAnswerSource,
          installmentAmount,
          currentInstallmentAmount,
        ),
      };
    },
  );

  const pastTaxesDisbursementOptions = () => {
    if (isTrackPlus) return PAST_TAX_DISBURSEMENT_OPTIONS.tracPlus;
    if (isRefinance) return PAST_TAX_DISBURSEMENT_OPTIONS.refinance;
    return PAST_TAX_DISBURSEMENT_OPTIONS.purchase;
  };

  const isDu =
    loanSummaryInfo.loanInfo.underwritingMethod === 'Desktop Underwriter';
  const isRateAndTerm = [
    REFI_PURPOSE_TYPE.RATE_AND_TERM_REDUCTION_CONVENTIONAL,
    REFI_PURPOSE_TYPE.RATE_AND_TERM_REDUCTION_FHA,
    REFI_PURPOSE_TYPE.RATE_AND_TERM_EXTENDED,
  ].includes(loanSummaryInfo?.propertyData?.refiPurpose);

  const hasDelinquentTaxes = (data.delinquentDueAtClosing ?? 0) > 0;

  const currentYear = new Date().getUTCFullYear();
  const previousYear = new Date().getUTCFullYear() - 1;

  const taxYearOptions = [
    { label: currentYear, value: currentYear },
    { label: previousYear, value: previousYear },
  ];
  const supplementalTaxesPayTypeOptions: Record<
    string,
    { label: string; value: number }[]
  > = {};
  (data.supplementalInstallments ?? []).forEach(
    ({ supplementalDate }, index) => {
      supplementalTaxesPayTypeOptions[`supplementalTaxes.${index}.paidType`] =
        getSupplementalTaxPaymentOptions({
          supplementalDate,
          effectiveClosingDate,
          firstPaymentDate,
          isTracLoan,
          isTaxEscrowWaived,
        });
    },
  );

  const escrowWaiverText = () =>
    `Escrow/Impound Waiver Type: ${
      WAIVE_ESCROW_OPTION_DISPLAY_NAMES[data.escrowWaiverOption] ??
      WAIVE_ESCROW_OPTION_DISPLAY_NAMES[1]
    }`;

  const dueDateText = () => {
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: 'UTC',
      month: '2-digit',
      day: '2-digit',
    });

    const uniqueDates = Array.from(
      new Set(
        (data.taxDueDates ?? []).map((d) => formatter.format(new Date(d))),
      ),
    ).sort((a, b) => a.localeCompare(b));

    return uniqueDates.length === 0 ? 'N/A' : uniqueDates.join(', ');
  };

  const annualAmountLabel = data.areTaxesBasedOnLandOnlyValue
    ? 'Land Only Annual Amount'
    : 'Annual Amount';

  const areTaxesBasedOnLandOnlyValue =
    data?.areTaxesBasedOnLandOnlyValue ?? null;
  const showDelinquentInstallmentAlert = false;
  const showPastTaxesDelinquentAlert = false;
  const showDelinquentExceedsThresholdAlert = false;
  const showPastDelinquentExceedsThresholdAlert = false;
  const additionalExceedsThreshold = false;
  const totalLoanAmount = loanSummaryInfo?.loanInfo.totalLoanAmount ?? 0;
  const showLPDelinquentInputAlert = false;
  const showDUDelinquentInputAlert = false;
  const showAdditionalTaxesPaidAtClosing = false;

  const cashToClose = data?.cashToClose ?? 0;
  const totalNonRealEstateDebt = data?.totalNonRealEstateDebt ?? 0;
  const pastTaxesPayTypeOptions = pastTaxesDisbursementOptions();

  return {
    ...futureTaxesDisabledState,
    ...installmentsPayTypeOptions,
    ...futureTaxesPayTypeOptions,
    ...supplementalTaxesPayTypeOptions,
    stateName: US_STATES_BY_ABBREVIATION[addressState],
    purchasePrice: salesPrice,
    disbursementOptions: defaultDisbursementOptions,
    additionalTaxesCount: data.additionalTaxes?.length ?? 0,
    payeeName: data.payeeName ?? '',
    initialData: getInitialFormValues(data, {
      installmentsPayTypeOptions,
      futureTaxesPayTypeOptions,
      supplementalTaxesPayTypeOptions,
      pastTaxesPayTypeOptions,
    }),
    disbursementLabel: data?.areTaxesBasedOnLandOnlyValue
      ? LAND_ONLY_AMOUNT
      : DISBURSEMENT_AMOUNT,
    pastTaxesDisbursementOptions: pastTaxesDisbursementOptions(),
    isRefinance,
    isRateAndTerm,
    hasDelinquentTaxes,
    hideExemptToggle,
    millageRateHint,
    totalValue,
    isDu,
    taxYearOptions,
    effectiveClosingDate,
    formattedEffectiveClosingDateStr: effectiveClosingDate.format('MM/DD/YYYY'),
    firstPaymentDate,
    isTracLoan,
    areTaxesBasedOnLandOnlyValue,
    annualAmountLabel,
    escrowWaiverText: escrowWaiverText(),
    showDelinquentInstallmentAlert,
    showPastTaxesDelinquentAlert,
    totalLoanAmount,
    showDelinquentExceedsThresholdAlert,
    showPastDelinquentExceedsThresholdAlert,
    additionalExceedsThreshold,
    isTrackPlus,
    isTaxEscrowWaived,
    showJobAidTip,
    showLPDelinquentInputAlert,
    showDUDelinquentInputAlert,
    showAdditionalTaxesPaidAtClosing,
    dueDateText: dueDateText(),
    cashToClose,
    totalNonRealEstateDebt,
    questionAnswers,
  };
};

export const formatSaveData = ({
  values,
  data,
}: {
  values: TaxesFormValues;
  data: DtoV1PropertyTaxEscrowDetails;
}) => {
  const parseAmount = (value: unknown) => {
    if (typeof value === 'number') return value;
    if (typeof value === 'string') {
      return Number.parseFloat(value.replaceAll(',', '')) || 0;
    }
    return 0;
  };

  const initialValues = getInitialFormValues(data);
  const hasChanges = isDirty(values, initialValues);
  const isMillageRateEnabled = Boolean(values.isMillageRate);
  const isSupplementalTaxesIncluded = Boolean(values.includeSupplementalTaxes);

  const installments = (values.installments ?? []).map(
    (installment, index) => ({
      ...data.escrowInstallments[index],
      ...installment,
      paidType: installment.paidType as DtoV1EnumTaxPaymentTypes,
    }),
  );
  const futureTaxes = (values.futureTaxes ?? []).map((futureTax) => ({
    ...data?.additionalTaxes.find(
      (tax) =>
        !tax.isPastInstallment && tax.installmentId === futureTax.installmentId,
    ),
    ...futureTax,
    paidType: futureTax.paidType as DtoV1EnumTaxPaymentTypes,
  }));
  const pastTaxes = (values.pastTaxes ?? []).map((pastTax) => ({
    ...data?.additionalTaxes.find(
      (tax) =>
        tax.isPastInstallment && tax.installmentId === pastTax.installmentId,
    ),
    ...pastTax,
    paidType: pastTax.paidType as DtoV1EnumTaxPaymentTypes,
  }));
  const additionalTaxes = [...futureTaxes, ...pastTaxes];
  const escrowInstallments = [...installments];
  const millageRate = isMillageRateEnabled ? +values.millageRate : 0;
  const taxRate =
    isMillageRateEnabled && millageRate === 0 ? +values.taxRate : 0;
  const supplementalTaxes = isSupplementalTaxesIncluded
    ? (values.supplementalTaxes ?? [])
    : [];
  const supplementalInstallments = supplementalTaxes.map(
    (installment, idx): DtoV1PropertyTaxSupplementalInstallment => ({
      taxId: data.taxId,
      supplementalId:
        installment.supplementalId ??
        data.supplementalInstallments?.[idx]?.supplementalId,
      supplementalAmount: installment.installmentAmount,
      supplementalDate: installment.distributionDate,
      paidType: installment.paidType as DtoV1EnumTaxPaymentTypes,
    }),
  );
  const savedSupplementalIds = new Set(
    supplementalInstallments
      .map(({ supplementalId }) => supplementalId)
      .filter(Boolean),
  );
  const formValues = { ...values };
  delete formValues.supplementalTaxes;
  delete formValues.includeSupplementalTaxes;
  return {
    fieldUpdateStatus: hasChanges,
    escrowDetails: {
      ...data,
      ...formValues,
      includeSupplementals: isSupplementalTaxesIncluded,
      millageRate,
      taxRate,
      additionalDueAtClosing: parseAmount(values.additionalTaxesPaidAtClosing),
      delinquentDueAtClosing: parseAmount(values.delinquentTaxAmount),
      supplementalDueAtClosing: parseAmount(values.supplementalDueAtClosing),
      supplementalDueAtClosingId: data.supplementalDueAtClosingId,
      nonAdValorum: values.isNonAdValorem ? values.nonAdValorem : 0,
      additionalTaxes: additionalTaxes,
      escrowInstallments: escrowInstallments,
      supplementalInstallments,
      deletedSupplementalInstallments: (data.supplementalInstallments ?? [])
        .map(({ supplementalId }) => supplementalId)
        .filter(
          (supplementalId) =>
            supplementalId && !savedSupplementalIds.has(supplementalId),
        ),
    },
  };
};

export const getTaxSummaryInitialValues = (
  data: DtoV1PropertyTaxGetTaxSummaryDto,
) => {
  if (!data) return {} as never;
  return {
    areTaxesBasedOnLandOnlyValue: convertBooleanToNullableString(
      data.areTaxesBasedOnLandOnlyValue,
    ),
    calculationMethod: convertNullableCalculationMethod(data.calculationMethod),
    taxRate: data.taxRate,
    millageRate: data.millageRate,
    annualQualifyingAmount: data.annualQualifyingAmount,
    generateShockLetter: data.generateShockLetter,
    isNewConstruction: data.isNewConstruction,
  };
};

export const getTaxSummaryScreenData = (
  loanData: LoanSummary | undefined,
  taxSummaryData?: DtoV1PropertyTaxGetTaxSummaryDto,
) => {
  const isRefi = loanData?.propertyData?.isRefi ?? false;
  const appraisedValue = loanData?.loanInfo.appraisedValue || 0;
  const salesPrice = loanData?.loanInfo.salesPrice || 0;
  const totalValue = isRefi ? appraisedValue : salesPrice;

  const totalHint = isRefi
    ? `Appraised Value ${formatCurrency(appraisedValue)}`
    : `Purchase Price ${formatCurrency(salesPrice)}`;

  return {
    isRefi: loanData?.propertyData?.isRefi ?? false,
    totalValue: totalValue,
    totalHint,
    yearBuilt: loanData?.propertyData?.yearBuilt || null,
    isNewConstruction: taxSummaryData?.isNewConstruction,
  };
};

export type TaxesSummaryFormValues = ReturnType<
  typeof getTaxSummaryInitialValues
>;

export const formatTaxSummarySaveData = (values: TaxesSummaryFormValues) => {
  const areTaxesBasedOnLandOnlyValue = convertStringToNullableBoolean(
    values.areTaxesBasedOnLandOnlyValue,
  );
  const calculationMethod = areTaxesBasedOnLandOnlyValue
    ? values.calculationMethod
    : null;
  const taxRate = calculationMethod === 'taxRate' ? values.taxRate : 0;
  const millageRate =
    calculationMethod === 'millageRate' ? values.millageRate : 0;
  const annualQualifyingAmount =
    calculationMethod === 'notProvided' || calculationMethod === null
      ? 0
      : values.annualQualifyingAmount;
  const generateShockLetter = areTaxesBasedOnLandOnlyValue
    ? values.generateShockLetter
    : false;

  return {
    areTaxesBasedOnLandOnlyValue,
    calculationMethod:
      convertCalculationMethodToNullableNumber(calculationMethod),
    taxRate,
    millageRate,
    annualQualifyingAmount,
    generateShockLetter,
  } as DtoV1PropertyTaxSaveTaxSummaryRequestDto;
};

const convertStringToNullableBoolean = (
  value: string | null,
): boolean | null => {
  if (value === 'yes') return true;
  if (value === 'no') return false;
  return null;
};
const convertBooleanToNullableString = (
  value: boolean | null,
): string | null => {
  if (value === true) return 'yes';
  if (value === false) return 'no';
  return null;
};

const convertNullableCalculationMethod = (
  value: number | null,
): 'manual' | 'millageRate' | 'taxRate' | 'notProvided' | null => {
  if (value === 0) return 'manual';
  if (value === 1) return 'taxRate';
  if (value === 2) return 'millageRate';
  if (value === 3) return 'notProvided';
  return null;
};

const convertCalculationMethodToNullableNumber = (
  value: 'manual' | 'millageRate' | 'taxRate' | 'notProvided' | null,
): number | null => {
  if (value === 'manual') return 0;
  if (value === 'taxRate') return 1;
  if (value === 'millageRate') return 2;
  if (value === 'notProvided') return 3;
  return null;
};

export const useDocumentOptions = (
  taxDocuments?: Array<{
    documentId?: number | null;
    documentTypeKey?: string | null;
  }> | null,
) => {
  return useMemo(
    () => ({
      documentId: (taxDocuments?.[0]?.documentId ?? 0).toString(),
      documentOptions: (taxDocuments ?? []).map((doc) => ({
        id: doc.documentId,
        label: TAX_DOC_TYPES_LABELS[doc.documentTypeKey] ?? 'Document',
      })),
    }),
    [taxDocuments],
  );
};
